import {
  Controller
} from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input"];

  connect() {
    this.boundHandlers = new WeakMap();
    this.observers = new WeakMap();
  }

  disconnect() {
    this.inputTargets.forEach((target) => this.cleanupTarget(target));
    this.boundHandlers = null;
    this.observers = null;
  }

  inputTargetConnected(target) {
    this.initializeTarget(target);
  }

  inputTargetDisconnected(target) {
    this.cleanupTarget(target);
  }

  initializeTarget(target) {
    if (!this.boundHandlers) {
      this.boundHandlers = new WeakMap();
    }

    target.style.resize = "none";

    const inputHandler = this.handleInput.bind(this);
    this.boundHandlers.set(target, inputHandler);
    target.addEventListener("input", inputHandler);

    if (this.isVisibleInDOM(target)) {
      this.captureInitialHeightAndAdjust(target);
    } else {
      this.observeVisibility(target);
    }
  }

  observeVisibility(target) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          this.captureInitialHeightAndAdjust(target);
          observer.disconnect();
          this.observers?.delete(target);
        }
      });
    });

    observer.observe(target);
    this.observers?.set(target, observer);
  }

  captureInitialHeightAndAdjust(target) {
    if (!target.dataset.textareaInitialHeight) {
      const computedStyle = window.getComputedStyle(target);
      const computedHeight = parseFloat(computedStyle.height);

      if (computedHeight > 0 && !isNaN(computedHeight)) {
        target.dataset.textareaInitialHeight = computedHeight.toString();
      }
    }

    this.adjustHeightToFitContent(target);
  }

  isVisibleInDOM(target) {
    return target.offsetHeight > 0;
  }

  cleanupTarget(target) {
    const inputHandler = this.boundHandlers?.get(target);
    if (inputHandler) {
      target.removeEventListener("input", inputHandler);
      this.boundHandlers.delete(target);
    }

    const observer = this.observers?.get(target);
    if (observer) {
      observer.disconnect();
      this.observers.delete(target);
    }
  }

  handleInput(event) {
    this.adjustHeightToFitContent(event.currentTarget);
  }

  adjustHeightToFitContent(target) {
    if (!target) return;

    const startingHeight = parseFloat(target.dataset.textareaInitialHeight);

    if (this.hasOnlyWhitespace(target)) {
      this.setHeightAndHideScrollbar(target, startingHeight);
      return;
    }

    target.style.height = `${startingHeight}px`;

    if (this.contentFitsWithinCurrentHeight(target, startingHeight)) {
      this.hideScrollbar(target);
      return;
    }

    const heightNeededForContent = this.measureHeightNeededForContent(target);
    const finalHeight = Math.max(startingHeight, heightNeededForContent);

    this.setHeightAndHideScrollbar(target, finalHeight);
  }

  hasOnlyWhitespace(target) {
    return target.value.trim() === "";
  }

  contentFitsWithinCurrentHeight(target, currentHeight) {
    return target.scrollHeight <= currentHeight;
  }

  measureHeightNeededForContent(target) {
    target.style.height = "1px";
    return target.scrollHeight;
  }

  setHeightAndHideScrollbar(target, height) {
    target.style.height = `${height}px`;
    this.hideScrollbar(target);
  }

  hideScrollbar(target) {
    target.style.overflowY = "hidden";
  }
}
